// Overrides de terminologia por empresa (tenant).
//
// Cada empresa que tiver preferencia de terminologia diferente do default
// adiciona uma entrada aqui mapeando keys de label para o texto que quer ver
// na UI. As keys sao livres mas devem ser usadas consistentemente nos
// templates via {{ 'key' | label }} (LabelPipe) ou via LabelService.get('key')
// no TypeScript.
//
// Empresas que aparecem em mais de um ambiente (DEV + PROD) devem listar os
// dois GUIDs (cada banco tem GUID proprio para a mesma empresa logica).
//
// Frases compostas tem key propria (em vez de interpolar a palavra solta)
// porque a troca Provedor -> Operadora muda o genero: artigos e participios
// precisam concordar ("do Provedor" -> "da Operadora", "vinculado" ->
// "vinculada"). Manter a frase inteira na key garante portugues correto.

export interface LabelOverrides {
  [key: string]: string;
}

// TEC GROUP nao quer a palavra "Provedor" em lugar nenhum da UH — quer
// "Operadora" (terminologia do RH deles). Inclui tabelas, menus e mensagens.
const TEC_GROUP_OVERRIDES: LabelOverrides = {
  // nucleo
  provedor: 'Operadora',
  provedores: 'Operadoras',
  provedorMin: 'operadora',
  provedorGeral: 'Operadora Geral',

  // titulos / textos de tela
  meusProvedores: 'Minhas Operadoras',
  provedoresCadastrados: 'Operadoras cadastradas',
  nenhumProvedorVinculado: 'Nenhuma operadora foi vinculada ainda.',
  provedoresDisponiveis: 'Operadoras disponíveis',
  selecioneProvedorPadrao:
    'Selecione uma operadora padrão para adicionar a lista de',
  nenhumProvedorCadastradoVincular:
    'Não há nenhuma Operadora cadastrada para ser vinculada.',
  carregandoProvedores: 'Carregando operadoras...',
  vincularEsteProvedor:
    'Complete os dados para vincular esta operadora à sua lista.',
  altereDadosProvedor: 'Altere os dados da operadora',
  carregandoProvedorSingular: 'Carregando Operadora...',
  dadosClienteNoProvedor: 'Dados do Cliente na Operadora',
  contatoNoProvedor: 'Contato na Operadora',
  dadosProvedor: 'Dados da Operadora',
  infoRelativasProvedor: 'Informações relativas à operadora',
  dadosAcessoPortalProvedor: 'Dados de acesso ao portal da operadora',
  noteCriarProvedorGeral:
    'Note: Ao criar uma <strong>Operadora Geral</strong> outros usuários poderão utilizar este cadastro para vinculá-la a uma <strong>Operadora</strong>.',

  // benefícios / produtos
  codigoNoProvedorLabel: 'Código Na Operadora',
  codigoNoProvedorHeader: 'Código na Operadora',
  codigoNoProvedorExport: 'Codigo Na Operadora',
  nomeProvedor: 'Nome da Operadora',
  razaoSocialProvedor: 'Razão Social da Operadora',
  exibindoBeneficiosProvedor: 'Exibindo benefícios cadastrados da operadora',
  beneficioProvedorSelecionado:
    'Insira as informações para adicionar um novo benefício para a operadora selecionada',

  // conciliação de fatura / rúbricas
  provedorContrato: 'Operadora / Contrato',
  valorGravadoContaProvedor:
    'O valor abaixo será gravado na conta de DÉBITO/CRÉDITO da operadora, para utilização futura:',
  batimentoGerouContaProvedor:
    'Este batimento gerou na conta de DÉBITO/CRÉDITO da operadora, o seguinte valor:',
  rubricaInterpretacaoProvedor:
    'Preencha as rúbricas para configurar a interpretação do arquivo de fatura de cada operadora',

  // integrações
  produtosAguardandoIntegracaoProvedor:
    'Produtos aguardando integração com a operadora',
  nenhumProdutoAguardandoIntegracaoProvedor:
    'Nenhum produto aguardando integração com a operadora foi encontrado.',
  integradoCriticaNoProvedor: 'Integrado - Crítica na operadora',
  integradoAguardandoRespostaProvedor:
    'Integrado - Aguardando resposta da operadora',

  // exportações / pedido de carga
  exportarLayoutProvedor: 'Exportar Layout da Operadora',
  filtrarExportarLayoutProvedor:
    'Insira as informações para Filtrar e Exportar o Layout da Operadora',
  exportacoesArquivosProvedor:
    'Listando as Exportações de arquivos para Operadora realizadas',
  gerarArquivoParaProvedor: 'Gerar aquivo para Operadora (.xlsx)',
  enviarPedidoAoProvedor: 'Enviar pedido à Operadora (API)',

  // menu lateral
  integracoesComProvedores: 'Integrações com Operadoras',
  exportacoesParaProvedor: 'Exportações para Operadora',

  // status (pipes)
  aguardandoProvedor: 'Aguardando Operadora',
  statusIntegradoCriticaNoProvedor: 'Integrado Critica Na Operadora',
  statusIntegradoAguardandoRespostaProvedor:
    'Integrado Aguardando Resposta Da Operadora',
  aguardandoComunicacaoProvedor: 'Aguardando comunicação com operadora',
  aguardandoAtivacaoProvedor: 'Aguardando ativação na operadora',

  // mensagens (toasts / loaders)
  provedorVinculadoSucesso: 'Operadora vinculada com sucesso!',
  provedorExcluidoSucesso: 'Operadora excluída com sucesso!',
  provedorAdicionadoSucesso: 'Operadora adicionada com sucesso',
  alterandoDadosProvedor: 'Alterando dados da Operadora...',
  buscandoMeusProvedores: 'Buscando minhas operadoras...',
  buscandoDadosProvedor: 'Buscando dados da Operadora...',
  buscandoDadosProvedorGeral: 'Buscando dados da Operadora Geral...',
  carregandoLancamentosProvedor: 'Carregando lançamentos da Operadora...',
  erroCarregamentoProvedorGeral:
    'Ops, houve um erro no carregamento da Operadora Geral',
  nenhumContratoParaProvedor:
    'Nenhum contrato foi encontrado para essa Operadora',
  semContratosVinculadosProvedor:
    'Não há contratos vinculados a essa operadora.',
  erroCarregarListaProvedores:
    'Ops, houve um erro ao carregar a lista de operadoras.',
  carregandoContratosProvedorSelecionado:
    'Carregando contratos da operadora selecionada...',
  enviandoPedidoIntegracaoProvedor:
    'Enviando pedido de integração à operadora...',
  dadosProvedorNaoCarregados:
    'Os dados da operadora ainda não foram carregados. Aguarde e tente novamente',
  carregandoDadosProvedorMin: 'Carregando dados da operadora...',
  erroDadosProvedor: 'Não foi possível carregar os dados da operadora',
  erroCarregamentoProvedor: 'Houve um erro no carregamento da Operadora.',
};

// IMPORTANTE: as keys (GUIDs) devem estar em MINUSCULAS. O LabelService
// normaliza o guid da empresa com toLowerCase() antes do lookup, porque o
// SQL Server devolve o uniqueidentifier em maiusculas.
export const EMPRESA_LABEL_OVERRIDES: Record<string, LabelOverrides> = {
  // TEC GROUP PARTICIPACOES LTDA — CNPJ 41.978.869/0001-92.
  // Mesmo GUID em DEV e PROD (registro preservado entre ambientes).
  '3f79372a-4db5-47a0-b62d-818e585d04a3': TEC_GROUP_OVERRIDES,
};

// Defaults usados quando a empresa logada nao tem override pra key solicitada.
// Texto identico ao que estava hardcoded antes — garante UI inalterada para
// todos os tenants que nao sao a Tec Group.
export const LABEL_DEFAULTS: LabelOverrides = {
  provedor: 'Provedor',
  provedores: 'Provedores',
  provedorMin: 'provedor',
  provedorGeral: 'Provedor Geral',

  meusProvedores: 'Meus Provedores',
  provedoresCadastrados: 'Provedores cadastrados',
  nenhumProvedorVinculado: 'Nenhum provedor foi vinculado ainda.',
  provedoresDisponiveis: 'Provedores disponíveis',
  selecioneProvedorPadrao:
    'Selecione um provedor padrão para adicionar a lista de',
  nenhumProvedorCadastradoVincular:
    'Não há nenhum Provedor cadastrado para ser vinculado.',
  carregandoProvedores: 'Carregando provedores...',
  vincularEsteProvedor:
    'Complete os dados para vincular este provedor à sua lista.',
  altereDadosProvedor: 'Altere os dados do provedor',
  carregandoProvedorSingular: 'Carregando Provedor...',
  dadosClienteNoProvedor: 'Dados do Cliente no Provedor',
  contatoNoProvedor: 'Contato no Provedor',
  dadosProvedor: 'Dados do Provedor',
  infoRelativasProvedor: 'Informações relativas ao provedor',
  dadosAcessoPortalProvedor: 'Dados de acesso ao portal do provedor',
  noteCriarProvedorGeral:
    'Note: Ao criar um <strong>Provedor Geral</strong> outros usuários poderão utilizar este cadastro para vinculá-lo a um <strong>Provedor</strong>.',

  codigoNoProvedorLabel: 'Código No Provedor',
  codigoNoProvedorHeader: 'Código no Provedor',
  codigoNoProvedorExport: 'Codigo No Provedor',
  nomeProvedor: 'Nome do Provedor',
  razaoSocialProvedor: 'Razão Social do Provedor',
  exibindoBeneficiosProvedor: 'Exibindo benefícios cadastrados do provedor',
  beneficioProvedorSelecionado:
    'Insira as informações para adicionar um novo benefício para o provedor selecionado',

  provedorContrato: 'Provedor / Contrato',
  valorGravadoContaProvedor:
    'O valor abaixo será gravado na conta de DÉBITO/CRÉDITO do provedor, para utilização futura:',
  batimentoGerouContaProvedor:
    'Este batimento gerou na conta de DÉBITO/CRÉDITO do provedor, o seguinte valor:',
  rubricaInterpretacaoProvedor:
    'Preencha as rúbricas para configurar a interpretação do arquivo de fatura de cada provedor',

  produtosAguardandoIntegracaoProvedor:
    'Produtos aguardando integração com o provedor',
  nenhumProdutoAguardandoIntegracaoProvedor:
    'Nenhum produto aguardando integração com o provedor foi encontrado.',
  integradoCriticaNoProvedor: 'Integrado - Crítica no provedor',
  integradoAguardandoRespostaProvedor:
    'Integrado - Aguardando resposta do provedor',

  exportarLayoutProvedor: 'Exportar Layout do Provedor',
  filtrarExportarLayoutProvedor:
    'Insira as informações para Filtrar e Exportar o Layout do Provedor',
  exportacoesArquivosProvedor:
    'Listando as Exportações de arquivos para Provedor realizadas',
  gerarArquivoParaProvedor: 'Gerar aquivo para Provedor (.xlsx)',
  enviarPedidoAoProvedor: 'Enviar pedido ao Provedor (API)',

  integracoesComProvedores: 'Integrações com Provedores',
  exportacoesParaProvedor: 'Exportações para Provedor',

  aguardandoProvedor: 'Aguardando Provedor',
  statusIntegradoCriticaNoProvedor: 'Integrado Critica No Provedor',
  statusIntegradoAguardandoRespostaProvedor:
    'Integrado Aguardando Resposta Do Provedor',
  aguardandoComunicacaoProvedor: 'Aguardando comunicação com provedor',
  aguardandoAtivacaoProvedor: 'Aguardando ativação no provedor',

  provedorVinculadoSucesso: 'Provedor vinculado com sucesso!',
  provedorExcluidoSucesso: 'Provedor excluído com sucesso!',
  provedorAdicionadoSucesso: 'Provedor adicionado com sucesso',
  alterandoDadosProvedor: 'Alterando dados do Provedor...',
  buscandoMeusProvedores: 'Buscando meus provedores...',
  buscandoDadosProvedor: 'Buscando dados do Provedor...',
  buscandoDadosProvedorGeral: 'Buscando dados do Provedor Geral...',
  carregandoLancamentosProvedor: 'Carregando lançamentos do Provedor...',
  erroCarregamentoProvedorGeral:
    'Ops, houve um erro no carregamento do Provedor Geral',
  nenhumContratoParaProvedor:
    'Nenhum contrato foi encontrado para esse Provedor',
  semContratosVinculadosProvedor:
    'Não há contratos vinculados a esse provedor.',
  erroCarregarListaProvedores:
    'Ops, houve um erro ao carregar a lista de provedores.',
  carregandoContratosProvedorSelecionado:
    'Carregando contratos do provedor selecionado...',
  enviandoPedidoIntegracaoProvedor:
    'Enviando pedido de integração ao provedor...',
  dadosProvedorNaoCarregados:
    'Os dados do provedor ainda não foram carregados. Aguarde e tente novamente',
  carregandoDadosProvedorMin: 'Carregando dados do provedor...',
  erroDadosProvedor: 'Não foi possível carregar os dados do provedor',
  erroCarregamentoProvedor: 'Houve um erro no carregamento do Provedor.',
};
